/**
 * History toolbox
 *
 * Detects whether the current page activation resulted from a browser back/forward navigation
 * (including BFCache restores) and exposes a promise-based API to consume the result.
 *
 * How it works:
 * - Registers a one-time 'pageshow' listener and inspects PageTransitionEvent.persisted and
 *   PerformanceNavigationTiming entries to determine a back/forward activation.
 * - Provides a singleton Promise that resolves with a boolean on the first pageshow.
 * - Adds a 5s timeout to reject the Promise if 'pageshow' does not arrive (e.g., late init).
 *
 * Usage:
 *   initBackForward();
 *   const isBF = await checkBackForward();
 *
 * Notes:
 * - initBackForward is idempotent and safe to call multiple times; only the first call wires listeners.
 * - The detection is performed once per page lifetime; subsequent calls reuse the same Promise.
 * - If initialization happens after 'pageshow' has already fired, the Promise may reject on timeout.
 */

/**
 * Determine whether a given activation looks like a back/forward navigation.
 * Inspects the PageTransitionEvent (BFCache) and Performance Navigation entries.
 *
 * @param event - The pageshow (or a fallback) event to inspect.
 * @returns true if back/forward navigation is detected; otherwise false.
 * @internal
 */
function detectBackForwardNavigation(event: PageTransitionEvent | Event): boolean {
    // bfcache
    if ((event as PageTransitionEvent)?.persisted) {
        return true;
    }

    try {
        const entries = performance.getEntriesByType?.('navigation') as Array<PerformanceNavigationTiming> | undefined;

        if (entries && entries[0]?.type === 'back_forward') {
            return true;
        }
    } catch {
        // no-op: older browsers may not support PerformanceNavigationTiming
    }

    try {
        // Deprecated but kept as a fallback for older browsers
        const nav = (performance as unknown as { navigation?: { type?: number } }).navigation;

        if (nav?.type === 2) {
            return true;
        }
    } catch {
        // no-op: ignore
    }

    return false;
}

/**
 * Singleton Promise containing the back/forward detection result.
 * Initialized on first call to initBackForward() and reused thereafter.
 */
let backForwardPromise: Promise<boolean> | null = null;

/**
 * Initialize back/forward navigation detection.
 *
 * Registers a one-time 'pageshow' listener and a 5-second timeout safeguard.
 * Idempotent: if already initialized, does nothing.
 *
 * The resulting Promise resolves to:
 * - true  => activation came from back/forward (including BFCache restore)
 * - false => activation did not come from back/forward
 *
 * The Promise rejects if the 5s timeout elapses before 'pageshow' fires.
 */
export function initBackForward(): void {
    if (backForwardPromise) {
        return; // already initialized
    }

    let timeoutId: number | undefined;

    backForwardPromise = new Promise<boolean>((resolve, reject) => {
        const onPageShow = (event: PageTransitionEvent | Event): void => {
            const isBF = detectBackForwardNavigation(event);

            resolve(isBF);

            window.removeEventListener('pageshow', onPageShow as EventListener);

            if (timeoutId !== undefined) {
                window.clearTimeout(timeoutId);
            }
        };

        // Resolve on pageshow (fires on initial load and BFCache restores)
        window.addEventListener('pageshow', onPageShow as EventListener, { once: true });

        // Fallback: resolve within 5 seconds using performance hints
        timeoutId = window.setTimeout(() => {
            reject(new Error('Back/forward navigation detection timed out'));
            window.removeEventListener('pageshow', onPageShow as EventListener);
        }, 5000);
    });
}

/**
 * Get the back/forward detection result Promise.
 * Automatically initializes detection if needed.
 *
 * @returns Promise<boolean> that resolves with the detection result or rejects on timeout.
 */
export function checkBackForward(): Promise<boolean> {
    if (!backForwardPromise) {
        initBackForward();
    }

    return (backForwardPromise as Promise<boolean>) || Promise.reject();
}
